import { Modal, Form, Input, InputNumber, message } from "antd";
import { useState, useEffect, useRef } from "react";
import { adminService } from "../../api/admin.service";
import { API_BASE_URL } from "../../api/constant";

const { TextArea } = Input;

function CourseModal({ isOpen, onClose, onSuccess, courseId = null, mode = "add" }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [originalPLink, setOriginalPLink] = useState(null);
  const fileInputRef = useRef(null);

  const isEditMode = mode === "edit" || courseId !== null;
  const modalTitle = isEditMode ? "Edit Course" : "Add New Course";
  const submitButtonText = isEditMode ? "Update Course" : "Add Course";
  const loadingText = isEditMode ? "Updating..." : "Adding...";

  useEffect(() => {
    if (isOpen && isEditMode && courseId) {
      fetchCourseData();
    } else if (isOpen && !isEditMode) {
      form.resetFields();
      setImagePreview(null);
      setImageFile(null);
    }
  }, [isOpen, courseId, isEditMode]);

  const fetchCourseData = async () => {
    setFetchingData(true);
    try {
      const result = await adminService.getCourseById(courseId);
      if (result.success) {
        form.setFieldsValue({
          course_name: result.data.course_name,
          instructor: result.data.instructor,
          price: result.data.price,
          description: result.data.description,
          y_link: result.data.y_link,
        });
        // Use the dedicated image endpoint for preview with cache busting
        setImagePreview(`${API_BASE_URL}/api/courses/${courseId}/course-image?t=${new Date().getTime()}`);
        setOriginalPLink(result.data.p_link);
        setImageFile(null);
      } else {
        message.error(result.error);
        onClose();
      }
    } catch {
      message.error("Failed to fetch course data");
      onClose();
    } finally {
      setFetchingData(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.error("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Image must be smaller than 5 MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (values) => {
    if (!imagePreview && !imageFile) {
      message.error("Please upload a course image");
      return;
    }

    setLoading(true);
    try {
      const courseData = {
        ...values,
        p_link: isEditMode ? originalPLink : values.y_link,
      };

      const result = isEditMode
        ? await adminService.updateCourse(courseId, courseData)
        : await adminService.createCourse(courseData);

      if (result.success) {
        const savedId = isEditMode ? courseId : result.data?.course_id;
        if (imageFile && savedId) {
          const uploadRes = await adminService.uploadCourseImage(savedId, imageFile);
          if (!uploadRes.success) {
            message.warning(`Course details saved, but image upload failed: ${uploadRes.error}`);
          }
        }
        message.success(isEditMode ? "Course updated successfully!" : "Course added successfully!");
        form.resetFields();
        setImagePreview(null);
        setImageFile(null);
        onClose();
        onSuccess?.();
      } else {
        message.error(result.error);
      }
    } catch {
      message.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setImagePreview(null);
    setImageFile(null);
    onClose();
  };

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="custom-modal"
      destroyOnClose
    >
      {fetchingData ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d7270]"></div>
          <span className="ml-3 text-gray-600">Loading course data...</span>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-2 space-y-4"
          initialValues={{ course_name: "", instructor: "", price: 0, description: "", y_link: "" }}
        >
          <Form.Item
            label="Course Name"
            name="course_name"
            rules={[
              { required: true, message: "Course name is required" },
              { min: 3, message: "Course name must be at least 3 characters" },
              { max: 100, message: "Course name cannot exceed 100 characters" },
            ]}
          >
            <Input placeholder="Enter course name" />
          </Form.Item>

          <Form.Item
            label="Instructor"
            name="instructor"
            rules={[
              { required: true, message: "Instructor is required" },
              { min: 2, message: "Instructor name must be at least 2 characters" },
            ]}
          >
            <Input placeholder="Enter instructor name" />
          </Form.Item>

          <Form.Item
            label="Price"
            name="price"
            rules={[
              { required: true, message: "Price is required" },
              { type: "number", min: 0, message: "Price must be a positive number" },
            ]}
          >
            <InputNumber
              placeholder="Enter price"
              className="w-full"
              min={0}
              step={0.01}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              { required: true, message: "Description is required" },
              { min: 10, message: "Description must be at least 10 characters" },
              { max: 500, message: "Description cannot exceed 500 characters" },
            ]}
          >
            <TextArea rows={4} placeholder="Enter course description" showCount maxLength={500} />
          </Form.Item>

          <Form.Item
            label="Video Link"
            name="y_link"
            rules={[
              { required: true, message: "Video link is required" },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          >
            <Input placeholder="https://example.com/video" />
          </Form.Item>

          {/* ── Course Image Upload ── */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              id="course-image-upload"
            />
            <label
              htmlFor="course-image-upload"
              className="cursor-pointer block"
            >
              <div
                className={`relative w-full h-44 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all duration-200 group
                  ${imagePreview
                    ? "border-[#1d7270]/40 bg-gray-50"
                    : "border-gray-300 bg-gray-50 hover:border-[#1d7270] hover:bg-[#1d7270]/5"
                  }`}
              >
                {imagePreview ? (
                  <>
                    <img
                      src={imagePreview}
                      alt="Course preview"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-1">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-white text-sm font-semibold">Change Image</span>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-400 group-hover:text-[#1d7270] transition-colors">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-semibold">Click to upload course image</span>
                    <span className="text-xs">PNG, JPG, WEBP up to 5 MB</span>
                  </div>
                )}
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-[#1d7270] text-white rounded-lg hover:bg-[#155857] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium min-w-[140px] flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {loadingText}
                </>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </Form>
      )}
    </Modal>
  );
}

export default CourseModal;
